/**
 * deviation.config.js
 *
 * ── What belongs here ────────────────────────────────────────────────────────
 *   ✓  modules        — drives the top header dropdown
 *   ✓  MONTHS         — fiscal year period labels for the Period filter
 *   ✓  dashboard metadata (label, description, apiKeys)
 *
 * ── What does NOT belong here ────────────────────────────────────────────────
 *   ✗  min / max threshold values
 *      These are configured per-KPI in the Trigger Module UI,
 *      stored in riu_reporting_question_target_data, synced by
 *      internalSyncGraphData() into riu_ai_dashboard_mapping, and
 *      returned as min_trigger_value / max_trigger_value in bi-graph/preview rows.
 *
 *   ✗  KPI / category / sub-category lists
 *      These are derived client-side from the bi-graph/preview response rows.
 *
 * ── Usage in DeviationDashboard ──────────────────────────────────────────────
 *   import deviationConfig, { MONTHS } from "./config/deviation.config";
 *   const configuredModules = deviationConfig.modules;   // top dropdown
 */

/** Modules shown in the top header dropdown. Must match backend module names exactly. */
const modules = ["Energy", "Water", "Waste", "Emission"];

const blank = (overrides = {}) => ({
  financial_year: [],
  location_id: [],
  sublocation_id: [],
  category: [],
  sub_category: [],
  kpi: [],
  displayPeriods: [],
  ...overrides,
});

/**
 * MONTHS — fiscal year period labels (Apr → Mar).
 * Must match the displayPeriods values returned by bi-graph/preview exactly.
 * ⚠️ If your API uses "April", "2024-04", or "M1" format, update this array.
 */
export const MONTHS = [
  "Apr", "May", "Jun", "Jul", "Aug", "Sep",
  "Oct", "Nov", "Dec", "Jan", "Feb", "Mar",
];

const graphConfigs = {
  Energy: {
    title: "Energy",
    description: "",
    chartType: "bar",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    widgetConfig: {
      module: ["Energy"],
      filters: blank({ category: ["Electricity Consumption", "Fuel Consumption"] }),
      group_by: ["kpi"],
      aggregate: { sum: "value" },
      aggregateSubLocations: true,
      sort: [{ field: "kpi", order: "asc" }],
    },
  },
  Electricity: {
    title: "Electricity",
    chartType: "pie",
    groupChartType: "column",
    enableStacking: true,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Electricity Consumption"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },
  Fuel: {
    title: "Fuel",
    chartType: "bar",
    groupChartType: "bar",
    enableStacking: false,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Fuel Consumption"] }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },
  Water: {
    title: "Water",
    chartType: "donut",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    widgetConfig: {
      module: ["Water"],
      filters: blank({
        category: [
          "Consumption",
          "Waste Water Generated",
          "Waste Water Treated",
          "Treated Water Reused",
        ],
      }),
      group_by: ["category"],
      aggregate: { sum: "value" },
      aggregateSubLocations: true,
      sort: [{ field: "category", order: "asc" }],
    },
  },
  Waste: {
    title: "Waste",
    chartType: "bar",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    dataConfigs: [
      {
        label: "Waste (Kg)",
        widgetConfig: {
          module: ["Waste"],
          filters: blank({
            category: ["Waste"],
            sub_category: ["Bio-Medical Waste", "Non-Hazardous Waste"],
          }),
          group_by: ["sub_category", "unit", "min_trigger_value", "max_trigger_value"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
          sort: [{ field: "sub_category", order: "asc" }],
        },
      },
      {
        label: "Waste (Litres)",
        widgetConfig: {
          module: ["Waste"],
          filters: blank({
            category: ["Waste"],
            sub_category: ["Hazardous Waste"],
          }),
          group_by: ["sub_category", "unit", "min_trigger_value", "max_trigger_value"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
          sort: [{ field: "sub_category", order: "asc" }],
        },
      },
    ],
  },
  Emission: {
    title: "Emission",
    chartType: "donut",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Emission"],
          filters: blank({ category: ["Emission"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },
};

const deviationConfig = {
  label:          "Deviation",
  description:    "KPI Deviation & Threshold Breach Dashboard",
  apiKeys:        ["deviation"],
  modules,
  graphConfigs,
  periods:        MONTHS,
};

export { modules, graphConfigs };
export default deviationConfig;
